import { useRef, useState } from 'react';

import useInView from 'Scripts/Hooks/useInView';

import { getNewDimensionsWithAspectRatio } from 'Utils/getNewDimensionsWithAspectRatio';

import { IImageBaseViewModel, INavigationViewModel } from 'Types/Litium';
import MegaMenuDropDown from 'Components/Header/MegaMenuDropDown';

import ArrowDownIcon from 'Icons/caretGsd.svg?react';

interface IMegaMenuProps extends INavigationViewModel {
    logoMini: IImageBaseViewModel;
    startPageUrl: string;
    showMiniLogoBreakPoint: boolean;
}

const MegaMenu = ({
    contentLinks,
    logoMini,
    startPageUrl,
    showMiniLogoBreakPoint,
}: IMegaMenuProps) => {
    const [showMenuIndex, setShowMenuIndex] = useState(-1);

    const toggleMegaMenu = (index: number) => {
        setShowMenuIndex(showMenuIndex === index ? -1 : index);
    };

    const handleFocusTrapForMegaMenu = (
        event: React.KeyboardEvent<HTMLButtonElement>
    ) => {
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                setShowMenuIndex(-1);
            }
        }
    };

    const megamenuRef = useRef<HTMLUListElement | null>(null);
    const { boundingClientRect } = useInView(megamenuRef);

    return (
        <nav
            className="justify-left hidden basis-full items-center lg:flex xl:mb-0"
            aria-label="Main Menu"
        >
            {logoMini?.src && (
                <a
                    href={startPageUrl}
                    className={`transition-[opacity, left] absolute duration-1000 ${
                        showMiniLogoBreakPoint
                            ? 'left-[56px] w-[38px] opacity-100'
                            : 'left-0 w-0 opacity-0'
                    }`}
                >
                    <img
                        className="hidden xl:flex xl:h-[35px]"
                        src={logoMini?.src}
                        alt={logoMini?.altText}
                        width={
                            logoMini?.width > 0
                                ? getNewDimensionsWithAspectRatio(
                                      logoMini.width,
                                      logoMini.height,
                                      35,
                                      'width'
                                  )
                                : ''
                        }
                    />
                </a>
            )}
            <ul
                ref={megamenuRef}
                style={
                    {
                        '--mega-menu-width': `${boundingClientRect?.width}px`,
                    } as React.CSSProperties
                }
                className={`flex flex-wrap items-center text-sm font-medium transition-all duration-1000 ${
                    showMiniLogoBreakPoint && `xl:ml-16`
                }`}
            >
                {contentLinks?.map((item, index) => (
                    <li
                        className="group/arrow flex items-center "
                        key={index}
                        onMouseEnter={() => toggleMegaMenu(index)}
                        onMouseLeave={() => toggleMegaMenu(-1)}
                    >
                        {item.links?.length > 0 ? (
                            <>
                                <a
                                    className={`flex shrink-0 cursor-pointer items-center px-1 pb-5 pt-4 hover:underline ${
                                        showMenuIndex === index
                                            ? 'underline'
                                            : ''
                                    } ${index === 0 ? 'pl-0' : ''}`}
                                    href={item.url}
                                >
                                    <span
                                        style={
                                            {
                                                '--text-color':
                                                    item?.textColourHexCode ||
                                                    'text-black',
                                            } as React.CSSProperties
                                        }
                                        className={`font-montserrat text-sm font-normal ${
                                            item.textColourHexCode.length > 0
                                                ? 'text-[var(--text-color)]'
                                                : 'text-black'
                                        } ${
                                            item.isSelected ||
                                            showMenuIndex === index
                                                ? 'underline'
                                                : ''
                                        }`}
                                    >
                                        {item.name}
                                    </span>
                                </a>
                                <button
                                    aria-controls={`mega-menu-dropdown-${index}`}
                                    aria-expanded={
                                        showMenuIndex === index
                                            ? 'true'
                                            : 'false'
                                    }
                                    className="px-1 pb-5 pt-4"
                                    onClick={() => toggleMegaMenu(index)}
                                    onKeyDown={event =>
                                        handleFocusTrapForMegaMenu(event)
                                    }
                                >
                                    <span className="sr-only">
                                        {showMenuIndex === index
                                            ? `${item.translations.close} ${item.name}`
                                            : `${item.translations.open} ${item.name}`}
                                    </span>
                                    <span className="block w-4">
                                        <span className="invisible block group-has-[:focus-visible]/arrow:visible ">
                                            <ArrowDownIcon
                                                className={`h-4 w-4 rotate-90 transition-all duration-layout ${
                                                    showMenuIndex === index &&
                                                    'rotate-[270deg]'
                                                }`}
                                            />
                                        </span>
                                    </span>
                                </button>
                                <MegaMenuDropDown
                                    menuId={`mega-menu-dropdown-${index}`}
                                    data={item.links}
                                    showMenu={showMenuIndex === index}
                                    setShowMenuIndex={setShowMenuIndex}
                                />
                            </>
                        ) : (
                            <a
                                style={
                                    {
                                        '--text-color':
                                            item.textColourHexCode ||
                                            'text-black',
                                    } as React.CSSProperties
                                }
                                className={`px-7 pb-5 pl-0 pt-4 font-montserrat text-sm font-normal ${
                                    item.textColourHexCode.length > 0
                                        ? 'text-[var(--text-color)]'
                                        : 'text-black'
                                }${item.isSelected ? ' underline' : ''}`}
                                href={item.url}
                            >
                                {item.name}
                            </a>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default MegaMenu;
